import React, { useEffect, useRef, useCallback } from "react"
import { createPortal } from "react-dom"

/**
 * Full-screen modal for human screening HITL. Esc closes (minimize); focus trap while open.
 */
export function HumanScreeningHitlModal({ isOpen, onClose, children, title = "Human screening" }) {
  const containerRef = useRef(null)
  const previouslyFocusedRef = useRef(null)

  const getFocusable = useCallback(() => {
    const root = containerRef.current
    if (!root) return []
    return Array.from(
      root.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    previouslyFocusedRef.current = document.activeElement
    const id = window.requestAnimationFrame(() => {
      const focusable = getFocusable()
      if (focusable.length) focusable[0].focus()
      else containerRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(id)
  }, [isOpen, getFocusable])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== "Tab" || !containerRef.current) return
      const focusable = getFocusable()
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isOpen, onClose, getFocusable])

  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) return
    const el = previouslyFocusedRef.current
    if (el && typeof el.focus === "function") {
      try {
        el.focus()
      } catch (_) {}
    }
    previouslyFocusedRef.current = null
  }, [isOpen])

  if (!isOpen || typeof document === "undefined") return null

  // z-index above legacy_fullscreen #generic-secondary-sidebar-wrapper (10002 !important when dev/superadmin navbars).
  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-stretch justify-stretch sm:items-center sm:justify-center bg-slate-900/55 backdrop-blur-sm p-0 sm:p-3 md:p-4"
      style={{ zIndex: 10020 }}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={containerRef}
        id="human-screening-hitl-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="human-screening-hitl-modal-title"
        tabIndex={-1}
        className="bg-white dark:bg-gray-900 dark:bg-gray-900 dark:bg-gray-900 flex flex-col overflow-hidden outline-none shadow-2xl w-full min-h-0 h-full max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(92vh,calc(100dvh-1.5rem))] sm:max-w-[min(96rem,calc(100vw-1.5rem))] sm:rounded-xl pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:pt-0 sm:pb-0 overscroll-y-contain text-gray-900 dark:text-gray-100 dark:text-gray-100"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-700/90 bg-slate-800 shrink-0">
          <h2
            id="human-screening-hitl-modal-title"
            className="text-base sm:text-lg font-semibold text-slate-50 m-0 min-w-0 pr-1 leading-snug"
          >
            {title}
          </h2>
          <button
            type="button"
            id="btn-human-screening-modal-minimize"
            className="text-slate-200 hover:text-white shrink-0 px-3 py-2 sm:py-1.5 rounded-lg border border-slate-500 bg-slate-700/60 text-sm font-medium hover:bg-slate-700 touch-manipulation min-h-[44px] sm:min-h-0"
            onClick={onClose}
          >
            Minimize
          </button>
        </div>
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-slate-50">{children}</div>
      </div>
    </div>,
    document.body
  )
}
